import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Phone,
  MessageCircle,
  Send,
  Mail,
  Calendar as CalIcon,
  FileText,
  Search,
  X,
  Save,
} from "lucide-react";
import api from "../lib/api";
import { phoneToWa } from "../lib/contact";
import Modal from "../components/Modal";
import DateTimePicker from "../components/DateTimePicker";
import QuickLogCall from "../components/QuickLogCall";
import VoiceTextarea from "../components/VoiceTextarea";
import { useAuth } from "../context/AuthContext";
import { usePersistedState } from "../lib/persisted";

const TYPES = {
  call: { label: "Call", icon: Phone, color: "#10B981" },
  whatsapp: { label: "WhatsApp", icon: MessageCircle, color: "#22C55E" },
  sms: { label: "SMS", icon: Send, color: "#3B82F6" },
  email: { label: "Email", icon: Mail, color: "#A78BFA" },
  meeting: { label: "Meeting", icon: CalIcon, color: "#F59E0B" },
  note: { label: "Note", icon: FileText, color: "#647087" },
};

const RANGES = [
  { v: "today", label: "Today" },
  { v: "week", label: "This week" },
  { v: "month", label: "This month" },
  { v: "all", label: "All time" },
];

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const startOfWeek = (d) => {
  const x = startOfDay(d);
  x.setDate(x.getDate() - x.getDay());
  return x;
};
const startOfMonth = (d) => {
  const x = startOfDay(d);
  x.setDate(1);
  return x;
};

export default function CallLogPage() {
  const { user } = useAuth();
  const [calls, setCalls] = useState([]);
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [range, setRange] = usePersistedState("calllog.range", "week");
  const [userFilter, setUserFilter] = usePersistedState("calllog.userFilter", "all");
  const [typeFilter, setTypeFilter] = usePersistedState("calllog.typeFilter", "all");
  const [q, setQ] = usePersistedState("calllog.q", "");
  const [activeCall, setActiveCall] = useState(null);

  const isManager = user && (user.role === "owner" || user.role === "admin" || user.role === "manager");

  const load = async () => {
    const [c, u, cust] = await Promise.all([
      api.get("/calls"),
      api.get("/users"),
      api.get("/customers"),
    ]);
    setCalls(Array.isArray(c.data) ? c.data : []);
    setUsers(Array.isArray(u.data) ? u.data : []);
    setCustomers(Array.isArray(cust.data) ? cust.data : []);
  };
  useEffect(() => {
    load();
    const refresh = () => load();
    window.addEventListener("calls:logged", refresh);
    return () => window.removeEventListener("calls:logged", refresh);
  }, []);

  const customerName = (id) => customers.find((c) => c.id === id)?.name || "—";
  const userName = (id) => users.find((u) => u.id === id)?.name || "—";

  const filtered = useMemo(() => {
    const now = new Date();
    let from = null;
    if (range === "today") from = startOfDay(now);
    else if (range === "week") from = startOfWeek(now);
    else if (range === "month") from = startOfMonth(now);

    return calls
      .filter((c) => {
        if (from) {
          const t = new Date(c.created_at);
          if (t < from) return false;
        }
        if (userFilter !== "all" && c.by_user_id !== userFilter) return false;
        if (typeFilter !== "all" && c.type !== typeFilter) return false;
        if (q) {
          const s = q.toLowerCase();
          const cn = customerName(c.customer_id).toLowerCase();
          if (
            !cn.includes(s) &&
            !(c.notes || "").toLowerCase().includes(s) &&
            !(c.by_user_name || "").toLowerCase().includes(s)
          )
            return false;
        }
        return true;
      })
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [calls, range, userFilter, typeFilter, q, customers]);

  // Per-user summary KPIs
  const summary = useMemo(() => {
    const map = {};
    for (const c of filtered) {
      const uid = c.by_user_id || "_unknown";
      map[uid] = (map[uid] || 0) + 1;
    }
    return Object.entries(map)
      .map(([uid, n]) => ({ uid, n, name: users.find((u) => u.id === uid)?.name || "Unknown" }))
      .sort((a, b) => b.n - a.n);
  }, [filtered, users]);

  const totalDuration = filtered.reduce((acc, c) => acc + (c.duration_min || 0), 0);

  return (
    <div className="space-y-5" data-testid="calllog-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#647087]">Activity</div>
          <h1 className="font-display text-2xl md:text-4xl font-bold tracking-tight mt-1 flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            Call log
            <span className="text-sm text-emerald-300/80 font-normal ml-1">{filtered.length}</span>
          </h1>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <QuickLogCall onLogged={load} variant="inline" label="Log call" />
        </div>
      </div>

      {/* KPIs */}
      <div className="grid sm:grid-cols-2 gap-3">
        <Kpi label="Activities in range" value={filtered.length} accent="#10B981" />
        <Kpi label="Reps active" value={summary.length} accent="#A78BFA" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#647087]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search customer, notes, rep…"
            className="hv-input hv-input-search"
            data-testid="calllog-search"
          />
        </div>
        <Pills
          value={range}
          options={RANGES.map((r) => [r.v, r.label])}
          onChange={setRange}
          testidPrefix="calllog-range"
        />
        {isManager && (
          <select
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
            className="hv-input py-2 text-xs w-44"
            data-testid="calllog-user"
          >
            <option value="all">All reps</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        )}
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="hv-input py-2 text-xs w-40"
          data-testid="calllog-type"
        >
          <option value="all">All types</option>
          {Object.entries(TYPES).map(([k, v]) => (
            <option key={k} value={k}>
              {v.label}
            </option>
          ))}
        </select>
      </div>

      {/* Per-rep summary chips */}
      {isManager && summary.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {summary.map((s) => (
            <button
              key={s.uid}
              onClick={() => setUserFilter(userFilter === s.uid ? "all" : s.uid)}
              className="px-3 py-1.5 rounded-full text-xs border transition"
              data-testid={`calllog-rep-${s.uid}`}
              style={{
                borderColor: userFilter === s.uid ? "#1FB6C1" : "rgba(255,255,255,0.1)",
                background: userFilter === s.uid ? "#1FB6C11a" : "transparent",
                color: userFilter === s.uid ? "#1FB6C1" : "#a0aabf",
              }}
            >
              {s.name} <span className="opacity-70">·</span>{" "}
              <span className="font-bold">{s.n}</span>
            </button>
          ))}
        </div>
      )}

      {/* List */}
      <div className="hv-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0a0b0e] border-b border-white/10">
            <tr className="text-left text-[#647087] text-xs uppercase tracking-wider">
              <th className="px-4 py-3">When</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Customer</th>
              <th className="px-4 py-3">Rep</th>
              <th className="px-4 py-3">Notes</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => {
              const t = TYPES[c.type] || TYPES.call;
              const Icon = t.icon;
              const cust = customers.find((x) => x.id === c.customer_id);
              const tel = cust?.phone ? cust.phone.replace(/[^0-9+]/g, "") : "";
              const wa = cust?.whatsapp ? cust.whatsapp.replace(/[^0-9]/g, "") : "";
              return (
                <tr
                  key={c.id}
                  className="border-b border-white/5 hover:bg-white/[0.03] transition cursor-pointer"
                  data-testid={`calllog-row-${c.id}`}
                  onClick={() => setActiveCall(c)}
                >
                  <td className="px-4 py-3 text-[#a0aabf] whitespace-nowrap">
                    <div className="text-xs">
                      {new Date(c.created_at).toLocaleDateString('en-GB')}
                    </div>
                    <div className="text-[10px] text-[#647087]">
                      {new Date(c.created_at).toLocaleTimeString('en-GB', {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider px-2 py-1 rounded-full border font-bold"
                      style={{
                        borderColor: `${t.color}66`,
                        background: `${t.color}1a`,
                        color: t.color,
                      }}
                    >
                      <Icon className="w-3 h-3" /> {t.label}
                      <span className="opacity-70">
                        · {c.direction === "in" ? "in" : "out"}
                      </span>
                      {c.duration_min ? <span className="opacity-70"> · {c.duration_min}m</span> : null}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {c.customer_id ? (
                      <Link
                        to={`/customers/${c.customer_id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-[#1fb6c1] hover:underline"
                      >
                        {customerName(c.customer_id)}
                      </Link>
                    ) : (
                      <span className="text-[#647087]">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[#dde3ec]">
                    {c.by_user_name || userName(c.by_user_id)}
                  </td>
                  <td className="px-4 py-3 text-[#a0aabf] max-w-md">
                    <div className="line-clamp-2 whitespace-pre-wrap">{c.notes || "—"}</div>
                  </td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1.5 justify-end">
                      {tel && (
                        <a
                          href={phoneToWa(tel) || "#"}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center w-7 h-7 rounded border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 transition"
                          data-testid={`calllog-tel-${c.id}`}
                          title={`Call ${cust.phone} (opens WhatsApp)`}
                        >
                          <Phone className="w-3.5 h-3.5" />
                        </a>
                      )}
                      {wa && (
                        <a
                          href={`https://wa.me/${wa}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center w-7 h-7 rounded border border-[#22C55E]/30 text-[#22C55E] hover:bg-[#22C55E]/10 transition"
                          data-testid={`calllog-wa-${c.id}`}
                          title="Open WhatsApp"
                        >
                          <MessageCircle className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="p-8 text-center text-[#647087]">
            No activity in this range. Hit the <span className="text-[#1fb6c1]">Log call</span> button in the top-right to add one.
          </div>
        )}
      </div>

      {activeCall && (
        <CallDetailModal
          call={activeCall}
          customer={customers.find((c) => c.id === activeCall.customer_id)}
          onClose={() => setActiveCall(null)}
          onSaved={() => {
            setActiveCall(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function CallDetailModal({ call, customer, onClose, onSaved }) {
  const t = TYPES[call.type] || TYPES.call;
  const [form, setForm] = useState({
    type: call.type,
    direction: call.direction,
    duration_min: call.duration_min || "",
    notes: call.notes || "",
    created_at: call.created_at,
  });
  const [saving, setSaving] = useState(false);
  const tel = customer?.phone ? customer.phone.replace(/[^0-9+]/g, "") : "";
  const wa = customer?.whatsapp ? customer.whatsapp.replace(/[^0-9]/g, "") : "";

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/calls/${call.id}`, {
        type: form.type,
        direction: form.direction,
        duration_min: form.duration_min ? Number(form.duration_min) : null,
        notes: form.notes,
        created_at: form.created_at,
      });
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="Call detail" testId="calllog-detail-modal" maxWidth="max-w-2xl">
      <div className="space-y-4">
        {/* Header strip */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ background: `${t.color}1a`, color: t.color, border: `1px solid ${t.color}66` }}
            >
              <t.icon className="w-5 h-5" />
            </div>
            <div>
              {customer ? (
                <Link
                  to={`/customers/${customer.id}`}
                  className="text-lg font-display font-bold text-[#1fb6c1] hover:underline"
                >
                  {customer.name}
                </Link>
              ) : (
                <div className="text-[#647087]">Unknown customer</div>
              )}
              <div className="text-xs text-[#647087]">
                Logged by{" "}
                <span className="text-white">{call.by_user_name}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {tel && (
              <a
                href={phoneToWa(tel) || "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="hv-btn hv-btn-ghost text-xs"
                data-testid="calllog-detail-tel"
                title="Call (opens WhatsApp)"
              >
                <Phone className="w-3.5 h-3.5" /> {customer.phone}
              </a>
            )}
            {wa && (
              <a
                href={`https://wa.me/${wa}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hv-btn hv-btn-ghost text-xs"
                data-testid="calllog-detail-wa"
                style={{ borderColor: "#22C55E66", color: "#22C55E" }}
              >
                <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
              </a>
            )}
          </div>
        </div>

        {/* Editable fields */}
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Type</label>
            <select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              className="hv-input mt-1"
              data-testid="calllog-detail-type"
            >
              {Object.entries(TYPES).map(([k, v]) => (
                <option key={k} value={k}>
                  {v.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Direction</label>
            <select
              value={form.direction}
              onChange={(e) => setForm({ ...form, direction: e.target.value })}
              className="hv-input mt-1"
            >
              <option value="out">Outbound</option>
              <option value="in">Inbound</option>
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Date & time</label>
            <DateTimePicker
              value={form.created_at}
              onChange={(v) => setForm({ ...form, created_at: v })}
              placeholder="Pick when this happened"
              testId="calllog-detail-when"
              className="mt-1"
            />
            <div className="text-[10px] text-[#647087] mt-1">
              Auto-stamped on logging — edit if you logged this after the fact.
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Duration (min)</label>
            <input
              type="number"
              value={form.duration_min}
              onChange={(e) => setForm({ ...form, duration_min: e.target.value })}
              className="hv-input mt-1"
              placeholder="optional"
            />
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-wider text-[#a0aabf]">
            Conversation / notes
          </label>
          <VoiceTextarea
            value={form.notes}
            onChange={(v) => setForm({ ...form, notes: v })}
            placeholder="Capture the full conversation, decisions, next steps… or tap the mic to dictate."
            rows={12}
            className="mt-1 leading-relaxed"
            context="lead_activity"
            testIdPrefix="calllog-detail-notes-voice"
            data-testid="calllog-detail-notes"
          />
          <div className="text-[10px] text-[#647087] mt-1">
            {form.notes.length} characters · supports long transcripts.
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={save}
            className="hv-btn hv-btn-primary flex-1"
            disabled={saving}
            data-testid="calllog-detail-save"
          >
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save changes"}
          </button>
          <button onClick={onClose} className="hv-btn hv-btn-ghost">
            <X className="w-4 h-4" /> Close
          </button>
        </div>

        <div className="text-[10px] text-[#647087] text-center pt-2 border-t border-white/5">
          🔒 Calls are <span className="text-amber-300">hard evidence</span> and cannot be deleted — only edited.
        </div>
      </div>
    </Modal>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div
      className="hv-card p-4 flex items-center justify-between"
      style={{ borderColor: `${accent}40`, background: `${accent}08` }}
    >
      <div className="text-xs uppercase tracking-wider text-[#a0aabf]">{label}</div>
      <div className="font-display text-3xl font-bold" style={{ color: accent }}>
        {value}
      </div>
    </div>
  );
}

function Pills({ value, options, onChange, testidPrefix }) {
  return (
    <div className="flex border border-white/10 rounded-md overflow-hidden">
      {options.map(([k, l]) => (
        <button
          key={k}
          onClick={() => onChange(k)}
          data-testid={`${testidPrefix}-${k}`}
          className={`px-3 py-2 text-xs uppercase tracking-wider ${
            value === k ? "bg-[#1fb6c1]/15 text-[#1fb6c1]" : "text-[#647087] hover:text-white"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
}
